import { BlockBlobClient } from "@azure/storage-blob";
import type { TransferProgressEvent } from "@azure/core-rest-pipeline";
import axios from "axios";

export interface UploadQueueItem {
  id: string;
  fileName: string;
  progress: number; // 0-1
  status: "uploading" | "done" | "error" | "cancelled";
  errorMessage?: string;
  etaSeconds?: number;
}

type Listener = () => void;

// simple pub-sub store, module-level singleton so uploads survive component
// unmounts caused by in-app navigation (this SPA has no router, so only a
// real browser navigation/reload tears this down)
class UploadQueueStore {
  private items: UploadQueueItem[] = [];
  private listeners: Listener[] = [];
  private controllers: Map<string, AbortController> = new Map();

  getItems(): UploadQueueItem[] {
    return this.items;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l());
  }

  private patch(id: string, changes: Partial<UploadQueueItem>) {
    this.items = this.items.map((it) =>
      it.id === id ? { ...it, ...changes } : it,
    );
    this.notify();
  }

  clearCompleted() {
    this.items = this.items.filter((it) => it.status === "uploading");
    this.notify();
  }

  cancel(id: string) {
    const controller = this.controllers.get(id);
    if (controller) {
      controller.abort();
    }
  }

  cancelAll() {
    this.items
      .filter((it) => it.status === "uploading")
      .forEach((it) => this.cancel(it.id));
  }

  // wipes the queue, e.g. on logout, so the widget doesn't resurface stale
  // items from a previous session when UploadManager remounts on re-login
  reset() {
    this.cancelAll();
    this.controllers.clear();
    this.items = [];
    this.notify();
  }

  // uploads a file directly to Azure Blob Storage via a SAS URL, in the background.
  // BlockBlobClient.uploadData stages the file as blocks (Put Block + Put Block List)
  // instead of one Put Blob request, so it isn't capped by Azure's ~4.77GB single-PUT limit.
  upload(
    file: File,
    opts: {
      projectNo: string;
      fileName?: string;
      libraryName?: string;
      userId?: string | number;
      itemBindID?: string | number;
      newbigFileName?: string;
      mdlIdRevision?: string;
    },
  ): Promise<{ blobName: string }> {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const controller = new AbortController();
    this.controllers.set(id, controller);
    this.items = [
      ...this.items,
      { id, fileName: opts.fileName || file.name, progress: 0, status: "uploading" },
    ];
    this.notify();

    const startTime = Date.now();

    const params = new URLSearchParams({
      fileName: opts.fileName || file.name,
      projectNo: opts.projectNo,
    });
    if (opts.libraryName) params.set("libraryName", opts.libraryName);
    if (opts.userId !== undefined) params.set("userId", String(opts.userId));
    if (opts.itemBindID !== undefined) params.set("itemBindID", String(opts.itemBindID));
    if (opts.newbigFileName) params.set("newbigFileName", opts.newbigFileName);
    if (opts.mdlIdRevision) params.set("mdlIdRevision", opts.mdlIdRevision);

    return axios
      .get(`/api/v1/azureBlob/uploadSasUrl?${params.toString()}`, {
        signal: controller.signal,
      })
      .then((sasRes) => {
        const { uploadUrl, blobName } = sasRes.data;
        const blockBlobClient = new BlockBlobClient(uploadUrl);

        return blockBlobClient
          .uploadData(file, {
            abortSignal: controller.signal,
            blockSize: 8 * 1024 * 1024,
            concurrency: 5,
            blobHTTPHeaders: {
              blobContentType: file.type || "application/octet-stream",
            },
            onProgress: (progress: TransferProgressEvent) => {
              const elapsedSeconds = (Date.now() - startTime) / 1000;
              const rate = progress.loadedBytes / elapsedSeconds;
              const etaSeconds =
                rate > 0 ? (file.size - progress.loadedBytes) / rate : undefined;
              this.patch(id, {
                progress: progress.loadedBytes / file.size,
                etaSeconds,
              });
            },
          })
          .then(() => {
            this.controllers.delete(id);
            this.patch(id, { progress: 1, status: "done" });
            return { blobName };
          });
      })
      .catch((err) => {
        this.controllers.delete(id);
        if (axios.isCancel(err) || err.name === "AbortError") {
          this.patch(id, { status: "cancelled", errorMessage: "Upload cancelled" });
        } else {
          this.patch(id, { status: "error", errorMessage: "Upload failed" });
        }
        throw err;
      });
  }
}

export const uploadQueueStore = new UploadQueueStore();
